<template>
    <div class="body v2-list-page">
        <div class="order-page-container">
            <div class="page-section">
                <a-card :bordered="false" style="width: 100%" class="v2-page-card">
                    <template slot="title">
                        <a-breadcrumb separator=">">
                            <a-breadcrumb-item>
                                <router-link to="/cliente/monitores/cotacoes">
                                    Home
                                </router-link>
                            </a-breadcrumb-item>
                            <template v-if="id">
                                <a-breadcrumb-item>
                                    <router-link to="/cliente/monitores/ressuprimento">
                                        Monitor de Ressuprimento
                                    </router-link>
                                </a-breadcrumb-item>
                                <a-breadcrumb-item>
                                    <router-link :to="backPath">
                                        Produtos do Ressuprimento #{{ id }}
                                    </router-link>
                                </a-breadcrumb-item>
                            </template>
                            <a-breadcrumb-item v-else>
                                <router-link :to="backPath">
                                    Produtos
                                </router-link>
                            </a-breadcrumb-item>
                            <a-breadcrumb-item class="order-page-title">
                                {{ product ? product.pro_descricao : "Produto" }}
                            </a-breadcrumb-item>
                        </a-breadcrumb>
                    </template>
                    <template slot="extra">
                        <a-button @click="goBack">
                            <i class="fa-solid fa-arrow-left"></i>
                            Voltar
                        </a-button>
                    </template>
                    <PageTabs v-if="product" :tabs="tabs" :active="activeTab" @change="openTab" />
                </a-card>
            </div>

            <div class="page-section">
                <a-card :bordered="false" style="width: 100%">
                    <a-spin v-if="loading" style="width: 100%; padding: 40px 0" />
                    <a-empty v-else-if="!product" description="Produto não encontrado" />
                    <template v-else>
                        <ProductFormV2Modal
                            v-if="activeTab === 'DATA' || activeTab === 'STORES'"
                            page
                            :section="activeTab"
                            :product="product"
                            @saved="onProductSaved"
                            @close="goBack"
                        />
                        <ProductLogV2Modal v-else-if="activeTab === 'LOG'" page :product="product" />
                        <ProductHistoryV2Modal v-else-if="activeTab === 'HISTORY'" page :product="product" />
                        <AddToGroupV2Modal v-else-if="activeTab === 'GROUPS'" page :product="product" />
                        <ProductBrandsV2Modal v-else-if="activeTab === 'BRANDS'" page :product="product" />
                        <ProductSuppliers
                            v-else-if="activeTab === 'SUPPLIERS'"
                            :sources="purchaseSources || []"
                            :stores="stores"
                            :loading="loadingPurchaseSources"
                            @updated="onPurchaseSourceUpdated"
                        />
                        <ProductEans v-else-if="activeTab === 'EANS'" :product="product" :eans="eans || []" :loading="loadingEans" />
                    </template>
                </a-card>
            </div>
        </div>
    </div>
</template>

<script>
import { perm_mixin } from "@/mixins/permission.mixin";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import ProductService from "@/services/v3/products/product.service";
import PageTabs from "@/components/cliente/monitors/PageTabs.vue";
import ProductFormV2Modal from "./modals/product-form-v2.modal.vue";
import ProductLogV2Modal from "./modals/product-log-v2.modal.vue";
import ProductHistoryV2Modal from "./modals/product-history-v2.modal.vue";
import AddToGroupV2Modal from "./modals/add-to-group-v2.modal.vue";
import ProductBrandsV2Modal from "./modals/product-brands-v2.modal.vue";
import ProductSuppliers from "./components/product-suppliers.vue";
import ProductEans from "./components/product-eans.vue";
import StoreService from "@/services/v3/stores/store.service";

export default {
    name: "ProductV2Page",
    mixins: [perm_mixin],
    components: {
        PageTabs,
        ProductFormV2Modal,
        ProductLogV2Modal,
        ProductHistoryV2Modal,
        AddToGroupV2Modal,
        ProductBrandsV2Modal,
        ProductSuppliers,
        ProductEans,
    },
    props: ["id", "productId"],
    data() {
        return {
            loading: false,
            product: null,
            activeTab: this.$route.query.tab,
            product_service: new ProductService(),
            purchaseSources: null,
            loadingPurchaseSources: false,
            stores: [],
            eans: null,
            loadingEans: false,
        };
    },
    computed: {
        backPath() {
            return this.id ? `/cliente/monitores/ressuprimento/editar/${this.id}` : "/produtos-v2";
        },
        tabs() {
            const canUpdate = this.can("PRODUCT", "UPDATE");
            return [
                { key: "DATA", label: "Dados do produto", icon: "edit", visible: canUpdate },
                { key: "STORES", label: "Sugestão por loja", icon: "store", visible: canUpdate },
                { key: "LOG", label: "Histórico de alterações", icon: "format_list_bulleted", visible: true },
                { key: "HISTORY", label: "Histórico do produto", icon: "inventory", visible: true },
                { key: "GROUPS", label: "Grupos", icon: "playlist_add", visible: canUpdate },
                { key: "BRANDS", label: "Marcas", icon: "category", visible: !!this.product && this.product.pro_generico != 0 },
                { key: "SUPPLIERS", label: "Fornecedores", icon: "local_shipping", visible: true },
                { key: "EANS", label: "EANs", icon: "qr_code", visible: true },
            ].filter((tab) => tab.visible);
        },
    },
    watch: {
        "$route.query.tab"(tab) {
            this.activeTab = tab;
            this.ensureValidTab();
        },
        activeTab() {
            this.loadTabData();
        },
    },
    methods: {
        goBack() {
            this.$router.push(this.backPath);
        },
        onProductSaved(product) {
            if (product) this.product = { ...this.product, ...product };
        },
        onPurchaseSourceUpdated(source) {
            if (!source || !this.purchaseSources) return;
            this.purchaseSources = this.purchaseSources.map((item) => (item.id === source.id ? source : item));
        },
        openTab(key) {
            this.activeTab = key;
            if (this.$route.query.tab !== key) {
                this.$router.replace({ query: { ...this.$route.query, tab: key } }).catch(() => {});
            }
        },
        ensureValidTab() {
            if (!this.tabs.some((tab) => tab.key === this.activeTab)) {
                this.activeTab = this.tabs.length ? this.tabs[0].key : undefined;
            }
        },
        async loadProduct() {
            const fromRoute = this.$route.params.product;
            if (fromRoute && String(fromRoute.pro_id) === String(this.productId)) {
                this.product = fromRoute;
                return;
            }
            this.loading = true;
            try {
                const response = await this.product_service.load(this.productId);
                this.product = response.data;
            } catch (error) {
                const status = error.response && error.response.status;
                if (status !== 404) ErrorHandlerService.handle(error, this.$store);
            } finally {
                this.loading = false;
            }
        },
        loadTabData() {
            if (!this.product) return;
            if (this.activeTab === "SUPPLIERS" && this.purchaseSources === null) this.loadPurchaseSources();
            if (this.activeTab === "EANS" && this.eans === null) this.loadEans();
        },
        async loadPurchaseSources() {
            this.loadingPurchaseSources = true;
            try {
                const [sources, stores] = await Promise.all([
                    this.product_service.purchase_sources(this.productId),
                    this.stores.length ? null : new StoreService().load(),
                ]);
                this.purchaseSources = sources.data || [];
                if (stores) {
                    const data = stores.data || [];
                    this.stores = (Array.isArray(data) ? data : data.data || []).map((store) => ({
                        loj_id: store.id,
                        loj_descricao: store.name,
                    }));
                }
            } catch (error) {
                this.purchaseSources = [];
                ErrorHandlerService.handle(error, this.$store);
            } finally {
                this.loadingPurchaseSources = false;
            }
        },
        async loadEans() {
            if (Array.isArray(this.product.eans)) {
                this.eans = this.product.eans;
                return;
            }
            this.loadingEans = true;
            try {
                const response = await this.product_service.load(this.productId);
                this.eans = response.data.eans || [];
            } catch (error) {
                this.eans = [];
                ErrorHandlerService.handle(error, this.$store);
            } finally {
                this.loadingEans = false;
            }
        },
    },
    async mounted() {
        await this.loadProduct();
        this.ensureValidTab();
        this.loadTabData();
    },
};
</script>

<style lang="scss" scoped>
@import "@/sass/commons/_list-page-v2";
</style>
